import { addDays, differenceInMinutes } from 'date-fns';
import { useCallback } from 'react';
import { HOUR_HEIGHT } from '../../constants';
import type { CalendarEvent, EventWithDimensions } from '../../types';

const MINUTE_MODIFIER = HOUR_HEIGHT / 60;

/**
 * Get overlapping events for a given time range
 */
const getOverlappingEvents = <T extends CalendarEvent>(
  events: Array<EventWithDimensions<T>>,
  top: number,
  bottom: number,
): Array<EventWithDimensions<T>> =>
  events.filter((otherEvent) => top < otherEvent.top + otherEvent.height && bottom > otherEvent.top);

/**
 * Get the column count for overlapping events
 */
const getColumnCount = <T extends CalendarEvent>(
  allEvents: Array<EventWithDimensions<T>>,
  previousOverlappingEvents: Array<EventWithDimensions<T>>,
): number => {
  const columnCount = Math.max(...previousOverlappingEvents.map((event) => event.left + 1), 0);

  const nextOverlappingEvents = allEvents.filter(
    (event) =>
      event.left >= columnCount &&
      getOverlappingEvents(previousOverlappingEvents, event.top, event.top + event.height).length > 0,
  );

  if (nextOverlappingEvents.length > 0) {
    return getColumnCount(allEvents, nextOverlappingEvents);
  }
  return columnCount;
};

/**
 * Calculate event positions with overlap handling
 * Adapted from the reference implementation to work with the current Calendar structure
 */
export const useCalculateDayEvents = () =>
  useCallback(<T extends CalendarEvent>({ day, events }: { day: Date; events: T[] }): Array<EventWithDimensions<T>> => {
    // Get day boundaries for filtering (midnight to end of day)
    const dayStartFilter = new Date(day);
    dayStartFilter.setHours(0, 0, 0, 0);

    // Calendar displays hour 0 (00:00-01:00) as the first row
    const dayEnd = addDays(day, 1);
    dayEnd.setHours(0, 0, 0, 0);

    // Calendar grid starts at hour 0 (see hours array), so we need a reference point for calculation
    const dayStartCalc = new Date(day);
    dayStartCalc.setHours(0, 0, 0, 0);

    // Filter and sort events for this day
    const dayEvents = events
      .filter((event) => event.start < dayEnd && event.end > dayStartFilter)
      .toSorted((a, b) => a.start.valueOf() - b.start.valueOf());

    // Calculate initial positions (top, height, left, width)
    const calculatedEvents: Array<EventWithDimensions<T>> = [];
    for (const event of dayEvents) {
      // Determine the actual start and end times for this day
      const calcStart = new Date(Math.max(event.start.getTime(), dayStartFilter.getTime()));
      const calcEnd = new Date(Math.min(event.end.getTime(), dayEnd.getTime()));

      // Skip events that end at or before the calendar start time (00:00)
      // Since the calendar displays from hour 0, events ending exactly at 00:00 shouldn't be shown
      if (calcEnd <= dayStartCalc) {
        continue;
      }

      // For positioning, clamp the start time to the calendar start (00:00) if it's earlier
      // This ensures events starting before 00:00 are displayed from the beginning of the visible calendar
      const positionStart = Math.max(calcStart.getTime(), dayStartCalc.getTime());

      // Calculate top position
      // Events are already in the correct timezone via getEventInTimezone
      // Calendar grid displays hours as [0, 1, 2, ..., 23]
      // So hour 0 is at position 0, hour 1 at 80px, ..., hour 23 at 1840px
      // Use positionStart to handle events that start before the calendar display start correctly
      const eventHour = new Date(positionStart).getHours();
      const eventMinutes = new Date(positionStart).getMinutes();

      // Hour N is at position N * 80px
      const top = Math.floor(eventHour * HOUR_HEIGHT + eventMinutes * MINUTE_MODIFIER);

      // Calculate height
      // Events are already in the correct timezone via getEventInTimezone
      // Use positionStart to calculate height from the actual displayed start position
      let height = differenceInMinutes(calcEnd, positionStart);
      height *= MINUTE_MODIFIER;
      height = Math.floor(height);

      const bottom = top + height;

      // Find overlapping events
      const overlappingEvents = getOverlappingEvents(calculatedEvents, top, bottom);

      // Assign left position (column) to avoid overlaps
      let left = 0;
      const usedLefts = new Set(overlappingEvents.map(({ left: previousLeft }) => previousLeft));
      while (usedLefts.has(left)) {
        left += 1;
      }

      calculatedEvents.push({
        ...event,
        height,
        left,
        top,
        width: 1,
      });
    }

    // Calculate column counts and widths
    const mappedCalculatedEvents = calculatedEvents.map((event) => {
      const columnCount = getColumnCount(
        calculatedEvents,
        getOverlappingEvents(calculatedEvents, event.top, event.top + event.height),
      );

      const width = 100 / columnCount;
      return { ...event, left: event.left * width, width };
    });

    // Adjust widths to prevent overlaps
    return mappedCalculatedEvents.map((event) => {
      const overlappingEvents = getOverlappingEvents(
        mappedCalculatedEvents.filter((otherEvent) => otherEvent.left > event.left),
        event.top,
        event.top + event.height,
      );

      if (overlappingEvents.length > 0) {
        const width = Math.min(...overlappingEvents.map((otherEvent) => otherEvent.left)) - event.left;
        return { ...event, width };
      }

      return event;
    });
  }, []);
